import { sanitizeHtml, isUsableUrl } from "@1-now/booking-core";
import { useData } from "data/data-provider";
import { FC, useState } from "react";
import { sectionPaddingY } from "utils/sectionSpacing";

export interface SectionClientSayProps {
  className?: string;
}

// ─────────────────────────────────────────────────────────────────
// Card variant — used by the GRID layout (1NOW-11175).
//
// A hairline card with the quote first and the person under a rule: photo (when the
// tenant gave one) and name. Only what the tenant entered: name, quote, photo; no rating and
// no "verified" label (1NOW-11140). The name comes first in the markup and the card is a
// reversed column, so it reads name then quote and draws quote then name.
// ─────────────────────────────────────────────────────────────────
const TestimonialCard: FC<{ item: any }> = ({ item }) => (
  <div className="db-card flex flex-col-reverse justify-end p-6">
    <div className="mt-5 flex items-center gap-3 border-t border-text-color/10 pt-4">
      {isUsableUrl(item?.image_url) && (
        <img
          src={item?.image_url}
          alt=""
          className="h-9 w-9 shrink-0 rounded-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      )}
      {/* h3: the section heading above is an h2, and jumping straight to
          h4 leaves a hole that breaks heading navigation. */}
      <h3 className="text-sm font-bold text-text-color">
        <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.heading) }} />
      </h3>
    </div>
    <p className="line-clamp-6 flex-1 text-[15px] leading-relaxed text-text-color">
      <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.description) }} />
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────
// Featured-quote variant — used by the CAROUSEL layout. One review at a time: the quote large
// and bold, the person under it. Nav is handled by the parent (arrows and dots).
// ─────────────────────────────────────────────────────────────────
const FeaturedTestimonial: FC<{ item: any }> = ({ item }) => (
  <div className="w-full shrink-0">
    <div className="flex w-full flex-col px-2 sm:px-4">
      {/* The quote. max-w keeps line lengths readable inside the full-width panel; line-clamp
          keeps a very long review from blowing up the height. `px-2` gives the clamp's
          overflow room so a trailing glyph is not clipped (1NOW-1247). */}
      <p className="mb-8 line-clamp-6 max-w-3xl px-2 text-lg font-semibold leading-snug text-text-color sm:text-xl md:text-2xl">
        <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.description) }} />
      </p>

      {/* The person: photo when there is one, and the name. */}
      <div className="flex items-center gap-3 border-t border-text-color/10 pt-4">
        {isUsableUrl(item?.image_url) && (
          <img
            src={item.image_url}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        <h3 className="text-base font-bold text-text-color">
          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.heading) }} />
        </h3>
      </div>
    </div>
  </div>
);

// Square chevron button — prev/next on the carousel.
const NavButton: FC<{
  direction: "prev" | "next";
  onClick: () => void;
  disabled?: boolean;
}> = ({ direction, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={direction === "prev" ? "Previous testimonial" : "Next testimonial"}
    className={`db-sq-btn ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
  >
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      {direction === "prev" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  </button>
);

/**
 * Reviews (1NOW-11175): a titled section of bold cards, drawn only when the tenant has some
 * (the home page asks testimonialsShown first). It shows what the tenant entered and nothing
 * more: no stars, no average, no "verified".
 */
const SectionTestimonials: FC<SectionClientSayProps> = ({ className = "" }) => {
  const { testimonials } = useData();
  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "grid"     → responsive card grid (default)
  //   "carousel" → featured-quote slider, one at a time
  const layoutVariant: "grid" | "carousel" =
    testimonials?.layout_variant === "carousel" ? "carousel" : "grid";
  const items: any[] = testimonials?.keypoints ?? [];

  // Carousel state — JS-driven `translateX` instead of native overflow-x so no browser
  // horizontal scrollbar leaks through.
  const [currentIndex, setCurrentIndex] = useState(0);
  const goTo = (i: number) =>
    setCurrentIndex(Math.max(0, Math.min(items.length - 1, i)));

  return (
    <div
      className={`nc-SectionClientSay relative ${className} ${sectionPaddingY(
        testimonials?.vertical_spacing,
        "py-10 sm:py-12 lg:py-14"
      )}`}
      data-nc-id="SectionClientSay"
    >
      <div>
        <div className="mb-6 sm:mb-8">
          <div className="db-eyebrow mb-2">Reviews</div>
          <h2 className="db-title [&_p]:m-0 [&_p]:inline">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(testimonials?.heading) }} />
          </h2>
        </div>

        {layoutVariant === "grid" && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {items.map((item: any) => (
              <TestimonialCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {/* Carousel layout — featured-quote slider in one bordered panel; JS-driven
            translateX advances the slides. Arrows and dots only with more than one review. */}
        {layoutVariant === "carousel" && items.length > 0 && (
          <div className="relative w-full">
            <div className="db-card relative overflow-hidden px-4 py-6 sm:px-10 sm:py-8">
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {items.map((item: any) => (
                    <FeaturedTestimonial key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>

            {items.length > 1 && (
              <div className="mt-6 flex items-center justify-center gap-4">
                <NavButton
                  direction="prev"
                  onClick={() => goTo(currentIndex - 1)}
                  disabled={currentIndex === 0}
                />

                {/* Dot pagination — each dot jumps to its slide; the active one is wider. */}
                <div className="flex items-center gap-2">
                  {items.map((item: any, i: number) => (
                    <button
                      key={item.id || i}
                      type="button"
                      onClick={() => goTo(i)}
                      aria-label={`Go to testimonial ${i + 1}`}
                      className={`h-2 rounded-sm transition-all ${
                        i === currentIndex
                          ? "w-7 bg-primary-600"
                          : "w-2 bg-text-color/25 hover:bg-text-color/45"
                      }`}
                    />
                  ))}
                </div>

                <NavButton
                  direction="next"
                  onClick={() => goTo(currentIndex + 1)}
                  disabled={currentIndex === items.length - 1}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionTestimonials;
